import React from "react";
import {
  Alert,
  Box,
  Button,
  ListItemText,
  ListItem,
  List,
  Snackbar,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ILead } from "../../../../../redux/leads/leads.types";
import { RightPanel } from "../../../../../components/RightPanel";
import { ManageLead } from "../../../../leads/ManageLead";
import { MODULES_UUIDS } from "../../../../../constants/enums";
import { IActionItem } from "../../../../../redux/actionItems/actionItem.types";

export interface IEditDetaislProps {
  title: string;
  open: boolean;
  leadDetails?: ILead | null;
  chatActionItem?: IActionItem | null;
  compactModeSaveTarget?: "lead" | "actionItem";
  shouldUsechatLeadData?: boolean;
  onShouldShowNoAdvanceSaveChange?: (value: boolean) => void;
  onClose: () => void;
  onComplete: (data: ILead) => void;
  onCancellationClick?: () => void;
  onCancelSuccess?: () => void
}

export const EditLeadDetails: React.FC<IEditDetaislProps> = (props) => {
  const {
    open,
    leadDetails,
    title,
    onComplete,
    onClose,
    onCancellationClick,
    chatActionItem,
    compactModeSaveTarget = "lead",
    shouldUsechatLeadData = false,
    onCancelSuccess
  } = props;

  const [isSaveDisabled, setIsSaveDisabled] = React.useState(false);
  const [showError, setShowError] = React.useState<any>(null);
  const [shouldShowNoAdvanceSave, setShouldShowNoAdvanceSave] =
    React.useState(false);
  const [showActivity, setShowActivity] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Same header toggle as ChatLeadQuickEditRightPanel. Existing leads show
  // their Activity; a new lead from an external lead shows its details.
  const hasLead = Boolean(leadDetails?.leads_uuid);
  const canShowActivity = hasLead || Boolean(chatActionItem);
  const activityLabel = hasLead ? "Activity" : "External Lead Details";
  const headerButtons = canShowActivity ? (
    <Button
      size="small"
      variant="outlined"
      color="inherit"
      onClick={(e) => {
        e.stopPropagation();
        setShowActivity((prev) => !prev);
      }}
      sx={{ color: "#fff", borderColor: "#fff", mr: 1, height: 28, fontSize: 12 }}
    >
      {showActivity ? `Hide ${activityLabel}` : `Show ${activityLabel}`}
    </Button>
  ) : undefined;

  const handleSaveClick = () => {
    if ((window as any).leadSaveHandler) {
      (window as any).leadSaveHandler();
    }
  };
  const handleShowErrors = (error: any) => {
    setShowError(error);
  };

  const handleSaveNoAdvanceClick = () => {
    if ((window as any).leadSaveNoAdvanceHandler) {
      (window as any).leadSaveNoAdvanceHandler();
    }
  };

  const handleCancellationClick = () => {
    if ((window as any).leadCancellationHandler) {
      (window as any).leadCancellationHandler();
    }

    if (onCancellationClick) {
      onCancellationClick();
    }
  };

  React.useEffect(() => {
    if (!open) {
      setShowError(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      (window as any).showLeadErrors = handleShowErrors;
    }

    return () => {
      delete (window as any).showLeadErrors;
    };
  }, [open]);

  return (
    <>
      <RightPanel
        open={open}
        heading={title}
        width={"95%"}
        onClose={onClose}
        topRightButtons={!isMobile ? headerButtons : undefined}
        subHeading={isMobile ? headerButtons : undefined}
        isWrappedWithForm
        actionButtons={() => (
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            sx={{
              gap: { xs: 1, sm: 2 },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Stack
              direction={"row"}
              spacing={{ xs: 1, sm: 2 }}
              justifyContent={"flex-start"}
            >
              <Button variant="contained" onClick={onClose}>
                Cancel
              </Button>
            </Stack>

            <Stack
              direction={"row"}
              spacing={{ xs: 1, sm: 2 }}
              justifyContent={"flex-end"}
            >
              {shouldShowNoAdvanceSave && (
                <Button
                  variant="contained"
                  sx={{
                    paddingX: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                  disabled={isSaveDisabled}
                  onClick={handleSaveNoAdvanceClick}
                >
                  Save (No Advance)
                </Button>
              )}

              <Button
                variant="contained"
                sx={{
                  paddingX: { xs: 2, sm: 4 },
                  borderRadius: 2,
                  backgroundColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
                disabled={isSaveDisabled}
                onClick={handleSaveClick}
              >
                Save
              </Button>
            </Stack>
          </Box>
        )}
      >
        {leadDetails && (
          <ManageLead
            moduleUuid={MODULES_UUIDS.LEADS}
            isCompactMode={true}
            chatActionItem={chatActionItem}
            chatLeadData={leadDetails}
            leadDetails={leadDetails}
            compactModeSaveTarget={compactModeSaveTarget}
            compactModeLeadUUID={leadDetails.leads_uuid || ""}
            compactModeLeadMeUUID={leadDetails.multiple_events_uuid || ""}
            hideActivityByDefault={true}
            showActivity={showActivity}
            onShowActivityChange={setShowActivity}
            shouldUsechatLeadData={shouldUsechatLeadData}
            onCompactModeComplete={(data) => {
              onComplete(data);
              onClose();
            }}
            onCompactModeCancellationClick={handleCancellationClick}
            onCompactModeSaveNoAdvanceClick={handleSaveNoAdvanceClick}
            onCompactModeSaveClick={handleSaveClick}
            onShouldShowNoAdvanceSaveChange={(value) =>
              setShouldShowNoAdvanceSave(value)
            }
            setSaveDisabled={setIsSaveDisabled}
            onCancelSuccess={onCancelSuccess}
          />
        )}
      </RightPanel>

      <Snackbar
        open={!!showError}
        autoHideDuration={5000}
        onClose={() => setShowError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setShowError(null)}
          severity="error"
          sx={{ width: 400 }}
        >
          <List dense sx={{ p: 0 }}>
            {showError &&
              Object.values(showError).map((msg, index) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <ListItemText primary={String(msg)} />
                </ListItem>
              ))}
          </List>
        </Alert>
      </Snackbar>
    </>
  );
};
